import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import AddButton from 'components/AddButton';
import EditActionButton from 'components/EditActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertPatientDocumentType from 'components/settings/cdr-settings/UpsertPatientDocumentType';
import React, { useEffect, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { getAllPatientDocumentTypes, getPatientDocumentType } from 'services/patientDocumentTypeServices';
import { PatientDocumentTypeDto, UpsertPatientDocumentTypeDto } from 'ts-types/api.types';
import { getText } from 'utils/textUtils';

const cancelTokenSource = axios.CancelToken.source();

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
}

const PatientDocumentTypeView = (props: Props) => {

  const {getMessage, language} = props;

  const [patientDocumentTypes, setPatientDocumentTypes] =
      useState<PatientDocumentTypeDto[]>([]);

  const [documentTypeCode, setDocumentTypeCode] = useState<string>();
  const [patientDocumentType, setPatientDocumentType] =
      useState<UpsertPatientDocumentTypeDto | undefined>();
  const [documentTypeModalOpen, setDocumentTypeModalOpen] =
      useState<boolean>(false);
  const [documentTypesLoaded, setDocumentTypesLoaded] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successfullyUploadedMessage, setSuccessfullyUploadedMessage] =
      useState<String | undefined>();

  const userPrivilege = props.currentUser ? props.currentUser.userConfig.privileges : [];
  const canEdit = userPrivilege.includes("PX_CORE_CLINICAL_DATA_RECORD");

  useEffect(() => {
    fetchPatientDocumentTypes();
  }, []);

  const fetchPatientDocumentTypes = async () => {

    setDocumentTypesLoaded(false);

    const onFinally = () => {
      setDocumentTypesLoaded(true);
    }

    try {
      const patientDocuments = await getAllPatientDocumentTypes(cancelTokenSource);
      setPatientDocumentTypes(patientDocuments);
    } finally {
      onFinally();
    }
  }

  const showPatientDocumentTypeModal = async (
      code?: string,
      patientDocumentType?: UpsertPatientDocumentTypeDto) => {

    setDocumentTypeModalOpen(true);
    setDocumentTypeCode(code);
    setPatientDocumentType(patientDocumentType);
  }

  const hidePatientDocumentTypeModal = () => {
    setDocumentTypeModalOpen(false)
  };

  const openUpsertDocumentTypeModal = (code?: string | undefined) => {

    if (!code) {
      return
    }

    setDocumentTypesLoaded(false);

    getPatientDocumentType(code, cancelTokenSource)
        .then(response => {
          showPatientDocumentTypeModal(code, response)
        }).catch((e: any) => {
          setErrorMessage(e.response.data)
        })
        .finally(() => {
          setDocumentTypesLoaded(true)
        })
  }

  const onSavePatientDocumentType = () => {
    const msg = getMessage("patientRecord.patientDocument.successfullyUploadedMessage");
    setSuccessfullyUploadedMessage(msg);

    fetchPatientDocumentTypes();

    hidePatientDocumentTypeModal();
  }


  const renderLoader = (): React.ReactNode => {

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("labTestComponent.loading")}</Loader>
        </Dimmer>
    );
  };

  const patientDocumentTypesRowGetter = ({index}: any) => {
    if (patientDocumentTypes) {
      Object.assign(patientDocumentTypes[index], {index: index + 1});
      return patientDocumentTypes[index];
    }
  }

  const patientDocumentTypesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role={"row"}
            style={style}
            onDoubleClick={() => openUpsertDocumentTypeModal(patientDocumentTypes && patientDocumentTypes[index].code)}
        >
          {columns}
        </div>
    )
  }

  const actionsCellRenderer = ({rowData}: any) => {
    if (canEdit) {
      return (
          <React.Fragment>
            <EditActionButton
                hoverMessage={getMessage('msgEdit')}
                onConfirm={() => openUpsertDocumentTypeModal(rowData.code)}
            />
          </React.Fragment>
      );
    }
    return <></>
  };

  const descriptionCellRenderer = ({rowData}: any) => {

    return (
        <React.Fragment>
          {getText(rowData.description, language)}
        </React.Fragment>
    );
  };

  const renderLabTestComponents = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={patientDocumentTypes.length}
            rowGetter={patientDocumentTypesRowGetter}
            rowRenderer={patientDocumentTypesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 500,
                label: getMessage("patientRecord.patientDocumentType.code"),
                dataKey: "code"
              },
              {
                width: 500,
                flexGrow: 1,
                label: getMessage("patientRecord.patientDocumentType.description"),
                dataKey: "description",
                cellRenderer: (descriptionCellRenderer)
              },
              {
                width: 100,
                label: getMessage("patientRecord.patientDocument.actionButtons"),
                dataKey: "code",
                cellRenderer: (actionsCellRenderer)
              }
            ]}
        />
    )
  }

  const renderUpsertPatientDocumentTypeModal = (): React.ReactNode => {
    return (
        <Modal
            open={documentTypeModalOpen}
            onClose={() => {
              hidePatientDocumentTypeModal()
            }}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("clinicData.home.searchBy.patientDocumentType")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertPatientDocumentType
                onCancel={() => {
                  hidePatientDocumentTypeModal()
                }}
                onSave={onSavePatientDocumentType}
                patientDocumentType={patientDocumentType}
                code={documentTypeCode}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  return (
      <PraxStyledSegment>
        {errorMessage
            && (
                <StyledErrorMessage>
                  {errorMessage}
                </StyledErrorMessage>
            )
        }
        <div>
          <PxGrid>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
              </Grid.Column>
              <Grid.Column width={4} verticalAlign={"top"}>
              </Grid.Column>
              <Grid.Column width={9}>
                {canEdit &&
                    <AddButton buttonMessage={getMessage("button.add")} onClick={() => showPatientDocumentTypeModal()}/>
                }
              </Grid.Column>
            </Grid.Row>
          </PxGrid>
        </div>
        <StyledViewTableRow>
          {!documentTypesLoaded ? renderLoader() : renderLabTestComponents()}
        </StyledViewTableRow>

        {renderUpsertPatientDocumentTypeModal()}
      </PraxStyledSegment>
  )
}

export default withAuthContext(withMessages(PatientDocumentTypeView));
